import { Link } from "react-router-dom";
import { Users, Dumbbell, Swords, Hammer, Trophy, Map, PawPrint, Crown, Shield, Target, Coins } from "lucide-react";
import { GoldCoin, FollisCoin } from "@/components/CoinIcons";

export default function Guide() {
  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <div className="text-center mb-8">
        <h1 className="font-cinzel text-4xl md:text-5xl text-amber-300 uppercase tracking-wide mb-2">
          How to Play Lanista Manager
        </h1>
        <p className="text-zinc-400 text-lg">A complete guide to building your gladiator empire</p>
      </div>

      <div className="space-y-8 text-zinc-300">
        {/* Getting Started */}
        <section className="bg-zinc-950 border border-amber-900/30 rounded p-6">
          <h2 className="font-cinzel text-2xl text-amber-300 mb-4 flex items-center gap-2">
            <Users size={24} /> Getting Started
          </h2>
          <div className="space-y-4">
            <div>
              <h3 className="text-amber-400 font-semibold mb-2">1. Create Your Account</h3>
              <p className="text-sm leading-relaxed">
                Sign up with your email to create your ludus (gladiator school). You'll start with gold to recruit your first gladiators immediately. No download required — play directly in your browser.
              </p>
            </div>
            <div>
              <h3 className="text-amber-400 font-semibold mb-2">2. Recruit Your First Gladiators</h3>
              <p className="text-sm leading-relaxed">
                Visit the Recruit Pool to scout fighters. Choose from six classes: Murmillo, Retiarius, Thraex, Hoplomachus, Secutor, and Dimachaerus. Each gladiator has hidden potential that reveals itself as you train them. Start with 2-3 fighters to build your initial roster.
              </p>
            </div>
            <div>
              <h3 className="text-amber-400 font-semibold mb-2">3. Set Up Training</h3>
              <p className="text-sm leading-relaxed">
                Go to Training and assign which stats each gladiator should focus on. Training occurs automatically at 00:00, 08:00, and 16:00 server time daily, even while you're offline. Balance strength, speed, stamina, defense, precision, technique, and aggression based on your gladiator's class and fighting style.
              </p>
            </div>
          </div>
        </section>

        {/* Core Gameplay */}
        <section className="bg-zinc-950 border border-amber-900/30 rounded p-6">
          <h2 className="font-cinzel text-2xl text-amber-300 mb-4 flex items-center gap-2">
            <Dumbbell size={24} /> Core Gameplay Loop
          </h2>
          <ol className="space-y-3 list-decimal list-inside">
            <li className="leading-relaxed"><strong className="text-amber-400">Train daily</strong> — Check back at training times to adjust stats and see your gladiators improve</li>
            <li className="leading-relaxed"><strong className="text-amber-400">Fight battles</strong> — Challenge other players in ranked PvP to climb the leaderboards</li>
            <li className="leading-relaxed"><strong className="text-amber-400">Earn rewards</strong> — Win gold, fragments, and skill points from victories</li>
            <li className="leading-relaxed"><strong className="text-amber-400">Forge equipment</strong> — Use fragments to craft weapons and armor in various rarity tiers</li>
            <li className="leading-relaxed"><strong className="text-amber-400">Manage roster</strong> — Retire aging gladiators and recruit fresh talent to keep your ludus strong</li>
          </ol>
        </section>

        {/* Combat System */}
        <section className="bg-zinc-950 border border-amber-900/30 rounded p-6">
          <h2 className="font-cinzel text-2xl text-amber-300 mb-4 flex items-center gap-2">
            <Swords size={24} /> Combat & Tactics
          </h2>
          <div className="space-y-4">
            <p className="leading-relaxed">
              Battles are tactical turn-by-turn simulations. Success depends on your gladiator's stats, equipment, skill tree unlocks, and chosen tactic (Aggressive, Balanced, or Defensive). Watch live commentary as the fight unfolds.
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <h3 className="text-amber-400 font-semibold mb-2 flex items-center gap-2"><Target size={16} /> Ranked PvP</h3>
                <p className="text-sm">Challenge opponents near your rating. Win to climb the leaderboards through Bronze, Silver, Gold, Platinum, and Diamond tiers.</p>
              </div>
              <div>
                <h3 className="text-amber-400 font-semibold mb-2 flex items-center gap-2"><Shield size={16} /> Scrimmages</h3>
                <p className="text-sm">Practice battles that don't affect your rating. Perfect for testing new equipment or tactics.</p>
              </div>
              <div>
                <h3 className="text-amber-400 font-semibold mb-2 flex items-center gap-2"><Trophy size={16} /> Tournaments</h3>
                <p className="text-sm">Weekly bracket-style competitions with special rewards. Single-elimination format — bring your best!</p>
              </div>
              <div>
                <h3 className="text-amber-400 font-semibold mb-2 flex items-center gap-2"><Crown size={16} /> Crown Challenges</h3>
                <p className="text-sm">Challenge the #1 ranked gladiator for the Golden Crown. Crown holders must defend against all challengers.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Equipment */}
        <section className="bg-zinc-950 border border-amber-900/30 rounded p-6">
          <h2 className="font-cinzel text-2xl text-amber-300 mb-4 flex items-center gap-2">
            <Hammer size={24} /> Equipment & Forging
          </h2>
          <p className="leading-relaxed mb-4">
            Collect weapon and armor fragments from victories. Visit the Blacksmith to forge equipment in five rarity tiers: Common, Uncommon, Rare, Elite, and Legendary. Higher rarities provide better stat bonuses.
          </p>
          <ul className="space-y-2 text-sm">
            <li className="flex items-start gap-2"><Coins size={14} className="text-amber-400 mt-1 shrink-0" /> Equipment degrades with use — repair items to maintain their effectiveness</li>
            <li className="flex items-start gap-2"><Coins size={14} className="text-amber-400 mt-1 shrink-0" /> Salvage unwanted gear for fragments to forge new equipment</li>
            <li className="flex items-start gap-2"><Coins size={14} className="text-amber-400 mt-1 shrink-0" /> Match equipment to your gladiator's class for maximum effectiveness</li>
            <li className="flex items-start gap-2"><Coins size={14} className="text-amber-400 mt-1 shrink-0" /> Elite and Legendary gear comes with powerful random affixes</li>
          </ul>
        </section>

        {/* Beasts */}
        <section className="bg-zinc-950 border border-amber-900/30 rounded p-6">
          <h2 className="font-cinzel text-2xl text-amber-300 mb-4 flex items-center gap-2">
            <PawPrint size={24} /> Beast System (Vivarium)
          </h2>
          <p className="leading-relaxed mb-4">
            Build a vivarium to house exotic beasts. Capture creatures through beast hunts, equip them to gladiators for combat bonuses, and breed them to create stronger offspring.
          </p>
          <div className="grid md:grid-cols-3 gap-3 text-sm">
            <div className="border border-teal-800/40 bg-teal-950/10 p-3 rounded">
              <strong className="text-teal-300">Atlas Bear</strong>
              <p className="text-xs text-zinc-500 mt-1">High strength & stamina</p>
            </div>
            <div className="border border-teal-800/40 bg-teal-950/10 p-3 rounded">
              <strong className="text-teal-300">Barbary Lion</strong>
              <p className="text-xs text-zinc-500 mt-1">Balanced combat stats</p>
            </div>
            <div className="border border-teal-800/40 bg-teal-950/10 p-3 rounded">
              <strong className="text-teal-300">Anatolian Leopard</strong>
              <p className="text-xs text-zinc-500 mt-1">Speed & precision</p>
            </div>
            <div className="border border-teal-800/40 bg-teal-950/10 p-3 rounded">
              <strong className="text-teal-300">Eurasian Wolf</strong>
              <p className="text-xs text-zinc-500 mt-1">Pack tactics bonus</p>
            </div>
            <div className="border border-teal-800/40 bg-teal-950/10 p-3 rounded">
              <strong className="text-teal-300">Nile Crocodile</strong>
              <p className="text-xs text-zinc-500 mt-1">Defense & power</p>
            </div>
            <div className="border border-teal-800/40 bg-teal-950/10 p-3 rounded">
              <strong className="text-teal-300">Barbary Macaque</strong>
              <p className="text-xs text-zinc-500 mt-1">Agility & evasion</p>
            </div>
          </div>
          <p className="text-sm mt-4 text-zinc-500">Breed two beasts to create offspring with combined traits. Complete breeding quests for rare genetic bonuses.</p>
        </section>

        {/* Imperial Campaign */}
        <section className="bg-zinc-950 border border-amber-900/30 rounded p-6">
          <h2 className="font-cinzel text-2xl text-amber-300 mb-4 flex items-center gap-2">
            <Map size={24} /> Imperial Campaign (PvE)
          </h2>
          <p className="leading-relaxed mb-4">
            Progress through provinces across the Roman Empire in single-player PvE mode. Each province has branching paths, unique AI opponents, and escalating difficulty.
          </p>
          <div className="grid md:grid-cols-2 gap-3 text-sm">
            <div><strong className="text-amber-400">Italia</strong> — Starting province, teaches the basics</div>
            <div><strong className="text-amber-400">Thracia</strong> — Intermediate challenges</div>
            <div><strong className="text-amber-400">Aegyptus (Egypt)</strong> — Advanced opponents</div>
            <div><strong className="text-amber-400">Britannia</strong> — Elite-tier gauntlet</div>
          </div>
          <p className="text-sm mt-4 text-zinc-500">Campaign uses a stamina system, so you can play at your own pace without feeling pressured.</p>
        </section>

        {/* Advanced Tips */}
        <section className="bg-zinc-950 border border-amber-900/30 rounded p-6">
          <h2 className="font-cinzel text-2xl text-amber-300 mb-4 flex items-center gap-2">
            <Trophy size={24} /> Advanced Tips
          </h2>
          <ul className="space-y-3 text-sm">
            <li className="leading-relaxed"><strong className="text-amber-400">Scout for potential:</strong> Check the Recruit Pool multiple times daily. Higher-potential gladiators are rarer but worth the investment.</li>
            <li className="leading-relaxed"><strong className="text-amber-400">Balance your roster:</strong> Mix experienced veterans with younger prospects to ensure long-term sustainability.</li>
            <li className="leading-relaxed"><strong className="text-amber-400">Unlock skill trees:</strong> Earn skill points from ranked fights. Unlock tactical nodes that provide permanent combat bonuses.</li>
            <li className="leading-relaxed"><strong className="text-amber-400">Join an alliance:</strong> Team up with other players for co-op events, shared treasury, and alliance chat.</li>
            <li className="leading-relaxed"><strong className="text-amber-400">Use the auction house:</strong> Buy and sell equipment, gladiators, and beasts to optimize your resources.</li>
            <li className="leading-relaxed"><strong className="text-amber-400">Complete daily quests:</strong> Timed quests refresh regularly and provide bonus rewards.</li>
            <li className="leading-relaxed"><strong className="text-amber-400">Watch battle replays:</strong> Learn from defeats by studying the turn-by-turn commentary to improve your tactics.</li>
          </ul>
        </section>

        {/* CTA */}
        <div className="text-center border border-amber-900/30 bg-amber-950/10 p-8 rounded">
          <h2 className="font-cinzel text-2xl text-amber-300 mb-4">Ready to Build Your Ludus?</h2>
          <p className="text-zinc-400 mb-6">Create your free account and start training gladiators today</p>
          <div className="flex gap-4 justify-center">
            <Link to="/login" className="bg-amber-600 hover:bg-amber-700 text-zinc-950 font-bold px-6 py-3 rounded transition-colors">
              Play Free Now
            </Link>
            <Link to="/faq" className="border border-amber-600 text-amber-400 hover:bg-amber-950/30 font-semibold px-6 py-3 rounded transition-colors">
              View FAQ
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
